import { set } from 'lodash';
import { getBannerForCampaign } from 'utils/banner/utils';
import { AreasDto, CampaignFields, CreateCampaignData, EditCampaignData } from 'utils/campaign/requirementUtils/types';
import { convertRegionsToDto, isValidAreaId, sourcesConfigToDto } from 'utils/campaign/utils';
import { BannerImagesData, EMPTY_ARRAY, getActiveCropTypes } from 'utils/clickme';
import {
    CAMPAIGN_MAX_AGE,
    CAMPAIGN_MIN_AGE,
    CampaignDeviceType,
    CampaignLanguage,
    CampaignProcess,
} from 'utils/constants/campaign';
import { prepareProfRolesOnSave } from 'utils/profRoles';

import { ProfRoles } from 'store/autoReducers';
import { BannerErrors, CreateBannerState } from 'store/models/createBanner';
import { CampaignErrors, CreateCampaignState } from 'store/models/createCampaign';
import { Campaign } from 'store/models/dashboard';

export type JointCampaignState = Partial<CreateCampaignState & CreateBannerState> & {
    profRoles: ProfRoles[];
};

export const mapCampaignAreasToDtoAreas = (
    areas: JointCampaignState['areas'],
    settings?: { default: Partial<JointCampaignState['areas']> }
): CreateCampaignData[CampaignFields.Areas] => ({
    include: convertRegionsToDto(areas?.include?.length ? areas.include : settings?.default?.include ?? EMPTY_ARRAY),
    exclude: convertRegionsToDto(areas?.exclude ?? settings?.default?.exclude ?? EMPTY_ARRAY),
});

export const mappingCampaignStateToDTO = (
    state: JointCampaignState
): Partial<EditCampaignData & CreateCampaignData> => {
    const {
        areas,
        adProfessions,
        adGender,
        adSource,
        adAgeFrom,
        adAgeTo,
        adTotalBudget,
        adTotalBudgetNoLimits,
        adDailyBudget,
        adSalary,
        adLanguage,
        adUnknownAge,
        adAutoSource,
        bid,
        profRoles,
        adCampaignTitle,
        campaignVacancyId,
        campaignProcess,
        campaignId,
        bannerVacancyId,
    } = state;

    const isEdit = campaignProcess === CampaignProcess.Edit;

    const activeCropTypes = adSource && getActiveCropTypes(adSource);
    const banner = activeCropTypes ? getBannerForCampaign(state as CreateBannerState, activeCropTypes) : undefined;

    return {
        [CampaignFields.Areas]: mapCampaignAreasToDtoAreas(areas),
        [CampaignFields.Sex]: adGender,
        [CampaignFields.Sources]: adSource && sourcesConfigToDto(adSource),
        [CampaignFields.AgeMin]: typeof adAgeFrom === 'number' ? adAgeFrom : CAMPAIGN_MIN_AGE,
        [CampaignFields.AgeMax]: typeof adAgeTo === 'number' ? adAgeTo : CAMPAIGN_MAX_AGE,
        [CampaignFields.Budget]: adTotalBudgetNoLimits ? null : adTotalBudget || null,
        [CampaignFields.BudgetDaily]: adDailyBudget || null,
        [CampaignFields.SalaryRange]: adSalary,
        [CampaignFields.Language]: adLanguage?.map((lang) => lang.toLowerCase()) as CampaignLanguage[],
        [CampaignFields.DeviceType]: CampaignDeviceType.Any,
        [CampaignFields.IncludeAnonymousAge]: adUnknownAge,
        [CampaignFields.AutoSource]: adAutoSource,
        [CampaignFields.ProfessionalRoles]: adProfessions ? prepareProfRolesOnSave(profRoles, adProfessions) : [],
        [CampaignFields.Bid]: bid,
        [CampaignFields.CampaignTitle]: isEdit ? adCampaignTitle : banner?.title,
        [CampaignFields.VacancyId]: isEdit ? campaignVacancyId : bannerVacancyId,
        ...(isEdit ? { [CampaignFields.CampaignId]: campaignId } : { [CampaignFields.Banner]: banner }),
    };
};

export type CampaignErrorsData = Partial<CampaignErrors & BannerErrors>;

export interface BannerError {
    bannerImages: BannerImagesData[] | string;
    title: string;
    text: string;
    url: string;
    classificationCodeId: string;
}

interface CampaignError {
    [CampaignFields.Areas]: string;
    [CampaignFields.ProfessionalRoles]: string;
    [CampaignFields.Sex]: string;
    [CampaignFields.AgeMin]: string;
    [CampaignFields.AgeMax]: string;
    [CampaignFields.Budget]: string;
    [CampaignFields.BudgetDaily]: string;
    [CampaignFields.CampaignTitle]: string;
    [CampaignFields.Sources]: string;
    [CampaignFields.Banner]: BannerError;
    [CampaignFields.CampaignId]: string;
    [CampaignFields.SalaryRange]: {
        noValues: string;
        invalidMin: string;
    };
}

export const mappingCampaignErrorsToState = (errorFields: Record<string, string>): CampaignErrorsData => {
    const errors: Partial<CampaignError> = {};

    Object.entries(errorFields).forEach(([key, value]) => {
        set(errors, key, value);
    });

    return {
        areas: errors.areas,
        adProfessions: errors.professionalRoles,
        adGender: errors.sex,
        adAgeFrom: errors.ageMin,
        adAgeTo: errors.ageMax,
        adSource: errors.campaignSources,
        adCampaignTitle: errors.title,
        campaignId: errors.id,
        adTotalBudget: errors.budget,
        adDailyBudget: errors.budgetDaily,
        bannerTitle: errors.banner?.title,
        bannerText: errors.banner?.text,
        bannerLink: errors.banner?.url,
        images: typeof errors.banner?.bannerImages === 'string' ? errors.banner?.bannerImages : undefined,
        classificationCodeId: errors.banner?.classificationCodeId,
        adSalaryCommon: errors.salaryRange?.noValues,
        adSalaryMin: errors.salaryRange?.invalidMin,
    };
};

export type CampaignDTO = Omit<Campaign, 'areas'> & { areas: AreasDto };

export const mapCampaignAreasFromDtoAreas = (
    areas: CampaignDTO['areas']
): { include: string[]; exclude: string[] } => ({
    include: areas.include.reduce<string[]>((result, region) => {
        if (isValidAreaId(region)) result.push(String(region));

        return result;
    }, []),
    exclude: areas.exclude.reduce<string[]>((result, region) => {
        if (isValidAreaId(region)) result.push(String(region));

        return result;
    }, []),
});

export const mappingCampaignStateFromDTO = (campaign: CampaignDTO): Campaign => ({
    ...campaign,
    areas: mapCampaignAreasFromDtoAreas(campaign.areas),
});
